import { z } from "zod";
import { IdempotencyKeySchema, IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
export const IncomingDocumentMimeSchema = z.enum(["application/pdf", "image/jpeg", "image/png"]);
export const IncomingReportCategorySchema = z.enum([
    "lab",
    "imaging",
    "consult",
    "discharge",
    "clinical",
    "administrative",
    "other",
]);
export const IncomingProcessingStatusSchema = z.enum([
    "reserved",
    "queued",
    "processing",
    "complete",
    "failed",
]);
export const IncomingReviewStatusSchema = z.enum([
    "administrative",
    "awaiting_routing",
    "awaiting_review",
    "communication_pending",
    "reviewed",
    "duplicate",
    "identity_conflict",
    "superseded",
]);
export const DocumentSourceSpanSchema = z
    .object({
    page: z.number().int().positive(),
    quote: z.string().min(1).max(12000),
})
    .strict();
/** Optional additions keep historical extractions readable; absent quality never authorizes fax filing. */
export const DocumentPatientIdentitySchema = z
    .object({
    name: z.string().max(500).nullable(),
    dateOfBirth: z.string().date().nullable(),
    mrn: z.string().max(160).nullable(),
    evidence: z.array(DocumentSourceSpanSchema).max(20),
    firstName: z.string().max(100).nullable().optional(),
    lastName: z.string().max(100).nullable().optional(),
    healthNumber: z.string().max(120).nullable().optional(),
    healthCardIssuer: z.string().max(120).nullable().optional(),
    healthCardVersion: z.string().max(10).nullable().optional(),
    phone: z.string().max(80).nullable().optional(),
    address: z.string().max(1000).nullable().optional(),
    quality: z.enum(["supported", "uncertain", "unreadable"]).nullable().optional(),
})
    .strict();
export const DocumentPhysicianIdentitySchema = z
    .object({
    role: z.enum(["attention", "ordering", "cc"]),
    name: z.string().max(500).nullable(),
    licenseNumber: z.string().max(80).nullable(),
    licenseJurisdiction: z.string().max(80).nullable(),
    billingNumber: z.string().max(80).nullable(),
    quality: z.enum(["supported", "uncertain", "unreadable"]),
    evidence: z.array(DocumentSourceSpanSchema).min(1).max(20),
})
    .strict();
export const DocumentValueSchema = z
    .object({
    label: z.string().min(1).max(500),
    code: z.string().max(160).nullable(),
    codeSystem: z.enum(["LOINC", "RadLex", "local"]).nullable(),
    bodySite: z.string().max(240).nullable(),
    laterality: z.enum(["left", "right", "bilateral", "not_applicable"]).nullable(),
    text: z.string().max(4000),
    numericValue: z.number().finite().nullable(),
    unit: z.string().max(120).nullable(),
    referenceRange: z.string().max(500).nullable(),
    flag: z.enum(["normal", "abnormal", "critical", "unknown"]),
    quality: z.enum(["supported", "uncertain", "unreadable"]),
    resultStatus: z.enum(["preliminary", "final", "corrected", "pending", "cancelled", "unknown"]),
    evidence: z.array(DocumentSourceSpanSchema).min(1).max(20),
})
    .strict();
export const DocumentChartProposalSchema = z
    .object({
    kind: z.enum(["pmh", "medication", "allergy", "other"]),
    description: z.string().min(1).max(4000),
    evidence: z.array(DocumentSourceSpanSchema).min(1).max(20),
})
    .strict();
/** Appointment notices are source facts, never inferred bookings or completed visits. */
export const DocumentAppointmentSchema = z
    .object({
    status: z.enum(["booked", "rescheduled", "cancelled"]),
    date: z.string().date().nullable(),
    time: z
        .string()
        .regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/)
        .nullable(),
    clinic: z.string().min(1).max(500).nullable(),
    specialty: z.string().min(1).max(160).nullable(),
    evidence: z.array(DocumentSourceSpanSchema).min(1).max(20),
})
    .strict();
/** A model-facing derivative; originals and exact evidence stay in the clinical record. */
export const DocumentModelContextSchema = z
    .object({
    policyVersion: z.literal("patient-privacy-v1"),
    sourcePages: z.array(z.number().int().positive()).min(1).max(1000),
    title: z.string().min(1).max(240),
    summary: z.string().min(1).max(12000),
    text: z
        .array(z.object({ page: z.number().int().positive(), markdown: z.string().max(100000) }).strict())
        .max(1000),
    values: z.array(DocumentValueSchema).max(100000),
    proposals: z.array(DocumentChartProposalSchema).max(20000),
    qualityIssues: z.array(z.string().max(2000)).max(20000),
    urgentEvidence: z.array(DocumentSourceSpanSchema).max(20000),
})
    .strict();
/** All dates/identifiers are transcribed from the source, never inferred from receipt time. */
export const ExtractedDocumentReportSchema = z
    .object({
    title: z.string().min(1).max(240),
    category: IncomingReportCategorySchema,
    pages: z.array(z.number().int().positive()).min(1).max(1000),
    continuesFromPrevious: z.boolean(),
    continuesToNext: z.boolean(),
    author: z.string().max(500).nullable(),
    reportDate: z.string().date().nullable(),
    studyDate: z.string().date().nullable(),
    statedStatus: z.enum(["preliminary", "final", "corrected", "unknown"]),
    externalReportId: z.string().max(240).nullable(),
    externalOrderId: z.string().max(240).nullable(),
    accessionNumber: z.string().max(240).nullable(),
    appointment: DocumentAppointmentSchema.nullable().optional(),
    patientIdentity: DocumentPatientIdentitySchema,
    providerIdentities: z.array(DocumentPhysicianIdentitySchema).max(30).nullable().optional(),
    /** A page with more than one patient's information cannot be filed by page splitting. */
    mixedPatientPages: z.array(z.number().int().positive()).max(1000).nullable().optional(),
    text: z
        .array(z.object({ page: z.number().int().positive(), markdown: z.string().max(100000) }).strict())
        .min(1)
        .max(1000),
    summary: z.string().min(1).max(12000),
    values: z.array(DocumentValueSchema).max(100000),
    qualityIssues: z.array(z.string().max(2000)).max(20000),
    proposals: z.array(DocumentChartProposalSchema).max(20000),
    urgent: z.boolean(),
    urgentEvidence: z.array(DocumentSourceSpanSchema).max(20000),
    modelContext: DocumentModelContextSchema.optional(),
})
    .strict();
export const DocumentExtractionBatchSchema = z
    .object({
    reports: z.array(ExtractedDocumentReportSchema).min(1).max(100),
})
    .strict();
export const DocumentReconciliationProposalSchema = z
    .object({
    relevance: z.string().min(1).max(4000),
    proposedMatches: z
        .array(z
        .object({
        expectedEventId: IdSchema,
        reason: z.string().min(1).max(2000),
        evidence: z.array(DocumentSourceSpanSchema).min(1).max(20),
    })
        .strict())
        .max(100),
    nextSteps: z
        .array(z
        .object({
        description: z.string().min(1).max(4000),
        evidence: z.array(DocumentSourceSpanSchema).min(1).max(20),
    })
        .strict())
        .max(100),
})
    .strict();
/**
 * Filing metadata an original carries when it comes from another system: what
 * the previous clinic called it, when the event happened there, and who
 * reviewed it. Only the migration channel sets it, and it is immutable.
 */
export const IncomingDocumentSourceSchema = z
    .object({
    system: z.string().min(1).max(160),
    reference: z.string().min(1).max(240),
    categoryHint: IncomingReportCategorySchema.optional(),
    title: z.string().max(240).optional(),
    effectiveDate: z.string().date().optional(),
    receivedAt: IsoDateTimeSchema.optional(),
    reviewedBy: z.string().max(160).optional(),
    reviewedAt: IsoDateTimeSchema.optional(),
    notes: z.string().max(4000).optional(),
    sendingFacilityReportId: z.string().max(240).optional(),
})
    .strict();
export const IncomingProcessingModeSchema = z.enum(["extract", "file_only"]);
export const IncomingOriginalSchema = z
    .object({
    id: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    patientId: IdSchema,
    encounterId: IdSchema.nullable(),
    uploaderId: IdSchema.nullable(),
    arrivalChannel: z.enum(["staff_upload", "fax", "migration"]),
    fileName: z.string().min(1).max(240),
    mimeType: IncomingDocumentMimeSchema,
    contentHash: Sha256Schema,
    byteSize: z.number().int().positive().max(250_000_000),
    bucket: z.literal("incoming-patient-documents"),
    objectName: z.string().min(1),
    receivedAt: IsoDateTimeSchema,
    scanStatus: z.enum(["not_performed", "clean"]),
    status: IncomingProcessingStatusSchema,
    version: z.number().int().positive(),
    generation: z.number().int().positive(),
    pageCount: z.number().int().positive().nullable(),
    completedPages: z.number().int().nonnegative(),
    failureCode: z.string().nullable(),
    /** Extraction may be complete while contextual matching is still queued. */
    reconciliationStatus: z
        .enum(["not_started", "pending", "processing", "complete", "failed"])
        .default("not_started"),
    /** Only terminally failed jobs accept the explicit retry operation. */
    retryAvailable: z.boolean().default(false),
    /** file_only files the bytes now; reprocess asks for extraction later. */
    processingMode: IncomingProcessingModeSchema.default("extract"),
    source: IncomingDocumentSourceSchema.nullish(),
})
    .strict()
    .superRefine((original, context) => {
    if (original.uploaderId === null && original.arrivalChannel !== "fax")
        context.addIssue({
            code: "custom",
            path: ["uploaderId"],
            message: "Only service-origin fax receipts may omit a human uploader",
        });
    if (original.arrivalChannel === "fax" && original.scanStatus !== "clean")
        context.addIssue({
            code: "custom",
            path: ["scanStatus"],
            message: "Fax originals require completed safe-file processing before chart publication",
        });
});
export const IncomingReportSchema = z
    .object({
    id: IdSchema,
    originalId: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    patientId: IdSchema,
    version: z.number().int().positive(),
    extractionVersion: z.number().int().positive(),
    supersedesReportId: IdSchema.nullable(),
    supersededByReportId: IdSchema.nullable(),
    extracted: ExtractedDocumentReportSchema,
    provenance: z
        .object({
        modelId: z.string(),
        releaseId: z.string(),
        promptVersion: z.string(),
        sourceHash: Sha256Schema,
        faxReceiptId: IdSchema.optional(),
        senderFax: z.string().nullable().optional(),
        sourcePages: z.array(z.number().int().positive()).optional(),
        copyPhysicianIds: z.array(IdSchema).nullish(),
    })
        .strict(),
    reviewStatus: IncomingReviewStatusSchema,
    ownerId: IdSchema.nullable(),
    identityResolved: z.boolean(),
    duplicateOf: IdSchema.nullable(),
    communicationRequired: z.boolean().nullable(),
    communicationRecorded: z.boolean(),
    disposition: z.string().nullable(),
    reviewedBy: IdSchema.nullable(),
    reviewedAt: IsoDateTimeSchema.nullable(),
    acknowledged: z.boolean(),
    possibleDuplicateIds: z.array(IdSchema),
})
    .strict();
export const IncomingDocumentDetailSchema = z
    .object({
    original: IncomingOriginalSchema,
    reports: z.array(IncomingReportSchema),
})
    .strict();
export const IncomingReportSummarySchema = IncomingReportSchema.extend({
    extracted: ExtractedDocumentReportSchema.pick({
        title: true,
        category: true,
        pages: true,
        author: true,
        reportDate: true,
        studyDate: true,
        statedStatus: true,
        summary: true,
        urgent: true,
    }),
});
export const IncomingDocumentListEntrySchema = z
    .object({ original: IncomingOriginalSchema, reports: z.array(IncomingReportSummarySchema) })
    .strict();
export const ReserveIncomingDocumentSchema = z
    .object({
    patientId: IdSchema,
    encounterId: IdSchema.optional(),
    fileName: z.string().trim().min(1).max(240),
    mimeType: IncomingDocumentMimeSchema,
    byteSize: z.number().int().positive().max(250_000_000),
    contentHash: Sha256Schema,
    idempotencyKey: IdempotencyKeySchema,
    /**
     * A caller declares a staff upload or a migration. `fax` belongs to the fax
     * connector and is never client-settable, and source metadata is accepted
     * only alongside `migration`.
     */
    arrivalChannel: z.enum(["staff_upload", "migration"]).optional(),
    processingMode: IncomingProcessingModeSchema.optional(),
    source: IncomingDocumentSourceSchema.optional(),
})
    .strict();
export const IncomingDocumentCommandSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    idempotencyKey: IdempotencyKeySchema,
})
    .strict();
export const IncomingReportCommandSchema = z.discriminatedUnion("operation", [
    IncomingDocumentCommandSchema.extend({ operation: z.literal("route"), ownerId: IdSchema }),
    IncomingDocumentCommandSchema.extend({
        operation: z.literal("filing"),
        title: z.string().min(1).max(240),
        category: IncomingReportCategorySchema,
        author: z.string().max(500).nullable(),
        reportDate: z.string().date().nullable(),
        studyDate: z.string().date().nullable(),
        reason: z.string().min(1).max(2000),
    }),
    IncomingDocumentCommandSchema.extend({
        operation: z.literal("identity"),
        reason: z.string().min(1).max(2000),
        confirmedSelectedPatient: z.literal(true),
    }),
    IncomingDocumentCommandSchema.extend({
        operation: z.literal("duplicate"),
        duplicateOf: IdSchema,
        reason: z.string().min(1).max(2000),
    }),
    IncomingDocumentCommandSchema.extend({ operation: z.literal("acknowledge") }),
    IncomingDocumentCommandSchema.extend({
        operation: z.literal("review"),
        disposition: z.string().min(1).max(4000),
        communicationRequired: z.boolean(),
        contextHash: Sha256Schema,
        followUpEventIds: z.array(IdSchema).max(100),
    }),
    IncomingDocumentCommandSchema.extend({
        operation: z.literal("communication"),
        note: z.string().min(1).max(4000),
    }),
    IncomingDocumentCommandSchema.extend({
        operation: z.literal("proposal"),
        proposalIndex: z.number().int().nonnegative(),
        decision: z.enum(["accept", "reject"]),
        reason: z.string().min(1).max(4000),
        contextHash: Sha256Schema,
        chartValue: z.record(z.string(), z.unknown()).optional(),
    }),
    IncomingDocumentCommandSchema.extend({
        operation: z.literal("follow_up"),
        summary: z.string().min(1).max(2000),
        dueAt: IsoDateTimeSchema,
        ownerId: IdSchema,
        contextHash: Sha256Schema,
    }),
    IncomingDocumentCommandSchema.extend({
        operation: z.literal("follow_up_status"),
        followUpId: IdSchema,
        state: z.enum(["in_progress", "completed", "cancelled"]),
        reason: z.string().min(1).max(2000),
        dueAt: IsoDateTimeSchema.nullable(),
    }),
    IncomingDocumentCommandSchema.extend({
        operation: z.literal("value"),
        valueIndex: z.number().int().nonnegative(),
        value: DocumentValueSchema,
        reason: z.string().min(1).max(2000),
    }),
    IncomingDocumentCommandSchema.extend({
        operation: z.literal("match"),
        expectedEventId: IdSchema,
        expectedEventVersion: z.number().int().positive(),
        decision: z.enum(["confirm", "reject"]),
        supportedCodes: z.array(z.string().min(1).max(240)).max(500),
        evidence: z.array(DocumentSourceSpanSchema).min(1).max(100),
        reason: z.string().min(1).max(2000),
    }),
]);
export const CorrectDocumentBoundariesSchema = IncomingDocumentCommandSchema.extend({
    groups: z
        .array(z
        .object({
        reportId: IdSchema.optional(),
        pages: z.array(z.number().int().positive()).min(1).max(1000),
    })
        .strict())
        .min(1)
        .max(1000),
    reason: z.string().min(1).max(2000),
});
export const DocumentFollowUpCommandSchema = z.discriminatedUnion("operation", [
    IncomingDocumentCommandSchema.extend({
        operation: z.literal("contact"),
        note: z.string().min(1).max(4000),
        outcome: z.enum(["attempted", "going_for_test", "declined", "needs_help", "other"]),
        nextFollowUpAt: IsoDateTimeSchema,
    }),
    IncomingDocumentCommandSchema.extend({
        operation: z.literal("discontinue"),
        reason: z.string().min(1).max(4000),
    }),
]);
export const DocumentTimingCommandSchema = z.discriminatedUnion("operation", [
    z
        .object({
        operation: z.literal("clinic_rule"),
        expectedVersion: z.number().int().nonnegative(),
        idempotencyKey: IdempotencyKeySchema,
        eventType: z.enum(["lab_result", "imaging_report", "consult_report"]),
        urgency: z.enum(["routine", "urgent", "stat"]),
        expectedHours: z.number().int().min(1).max(87600),
        overdueHours: z.number().int().min(1).max(87600),
        staffOwnerId: IdSchema,
    })
        .strict(),
    IncomingDocumentCommandSchema.extend({
        operation: z.literal("override"),
        id: IdSchema,
        staffOwnerId: IdSchema.nullable().optional(),
        urgency: z.enum(["routine", "urgent", "stat"]),
        plannedAt: IsoDateTimeSchema.nullable(),
        dependencyEventId: IdSchema.nullable(),
        expectedBy: IsoDateTimeSchema.nullable(),
        overdueAt: IsoDateTimeSchema.nullable(),
    }),
]);
//# sourceMappingURL=incoming-documents.js.map